/**
 * Analytics presentation logic — pure, no React, no server. Holds the per-metric metadata (i18n key,
 * unit + formatter, the rapport tone-band thresholds + direction), the period→date-window resolver,
 * and the number formatters. Tone bands MUST resolve to color + glyph + text downstream (never color
 * alone — daltonisme + médico-légal); this module only decides the band, the chip injects the glyph.
 */
import { token } from "@/design/theme";
import { ANALYTICS_CAPS } from "./caps";
import type { HistoryMetric, MetricType, Period, ToneBand } from "./types";

/** How a metric value reads as a number. */
export type MetricFormat = "count" | "percent" | "currency_da" | "decimal";

export interface MetricMeta {
  /** i18n key suffix (analytics.metric.<key>). */
  key: MetricType;
  format: MetricFormat;
  /** true = higher is better (volume, revenue); false = lower is better (qc reject, reagent spend, TAT). */
  higherIsBetter: boolean;
  /**
   * Tone-band thresholds, interpreted on the value. For higherIsBetter the bands read
   * value >= good → good, >= watch → watch, else alert; for lower-is-better the comparison flips.
   * `null` thresholds mean the metric is informational (always neutral/good — e.g. raw volume has no
   * universal "alert" line; it shows as good and the trend carries the story).
   */
  good: number | null;
  watch: number | null;
  /** Display order on the tile grid (lower = first). */
  order: number;
}

/**
 * The v1 metric catalogue. Unknown server metric_types still render via metaFor()'s fallback, so the
 * dashboard never drops a tile the server adds later (AN-1 "the rest follow the same shape").
 */
export const METRIC_META: Readonly<Record<string, MetricMeta>> = Object.freeze({
  tat_signoff: { key: "tat_signoff", format: "decimal", higherIsBetter: false, good: 240, watch: 480, order: 10 },
  volume_demandes: { key: "volume_demandes", format: "count", higherIsBetter: true, good: null, watch: null, order: 20 },
  volume_resultats: { key: "volume_resultats", format: "count", higherIsBetter: true, good: null, watch: null, order: 30 },
  revenu_caisse: { key: "revenu_caisse", format: "currency_da", higherIsBetter: true, good: null, watch: null, order: 40 },
  qc_rejet_taux: { key: "qc_rejet_taux", format: "percent", higherIsBetter: false, good: 2, watch: 5, order: 50 },
  reactif_depense: { key: "reactif_depense", format: "decimal", higherIsBetter: false, good: null, watch: null, order: 60 },
});

const FALLBACK_META = (key: MetricType): MetricMeta => ({
  key,
  format: "decimal",
  higherIsBetter: true,
  good: null,
  watch: null,
  order: 900,
});

export function metaFor(metricType: MetricType): MetricMeta {
  return METRIC_META[metricType] ?? FALLBACK_META(metricType);
}

/**
 * NOVA-034 — the per-band capability map, MIRRORING server/src/config/analytics.config.js bands.bandByMetric.
 * Each metric_type maps to the analytics.<band>.read cap that gates its tile. The server already strips bands
 * the actor cannot read (so a missing tile is the true contract); this is the belt-and-braces client gate so
 * a tile never renders without its cap even if the server is permissive (e.g. an internal/E2E call). An
 * unknown metric rides the operational `tat` band (Manager-default).
 */
const BAND_CAP_BY_METRIC: Readonly<Record<string, string>> = Object.freeze({
  tat_signoff: ANALYTICS_CAPS.tatRead,
  volume_demandes: ANALYTICS_CAPS.tatRead,
  volume_resultats: ANALYTICS_CAPS.tatRead,
  qc_rejet_taux: ANALYTICS_CAPS.qcRead,
  revenu_caisse: ANALYTICS_CAPS.financeRead,
  reactif_depense: ANALYTICS_CAPS.financeRead,
});

/** The analytics.<band>.read cap a metric_type's tile is gated on (falls back to the tat band). */
export function bandCapFor(metricType: MetricType): string {
  return BAND_CAP_BY_METRIC[metricType] ?? ANALYTICS_CAPS.tatRead;
}

/** Resolve the rapport tone band a value falls into for its metric. Informational metrics → "good". */
export function toneFor(meta: MetricMeta, value: number): ToneBand {
  if (meta.good == null || meta.watch == null) return "good";
  if (meta.higherIsBetter) {
    if (value >= meta.good) return "good";
    if (value >= meta.watch) return "watch";
    return "alert";
  }
  // lower is better
  if (value <= meta.good) return "good";
  if (value <= meta.watch) return "watch";
  return "alert";
}

/** Token foreground per tone band (mirrors the success/warn/danger triad). */
export const TONE_COLOR: Readonly<Record<ToneBand, string>> = Object.freeze({
  good: token.success,
  watch: token.warn,
  alert: token.danger,
});

/** Mandated shape glyph per tone band (carries meaning without color). */
export const TONE_GLYPH: Readonly<Record<ToneBand, string>> = Object.freeze({
  good: "●",
  watch: "▲",
  alert: "◆",
});

// ── Number formatting ────────────────────────────────────────────────────────────

const nfInt = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
const nfDec = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 });
const nfPct = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });

/** Format a metric value for display. Currency is stored in centimes (server: paiement.montant). */
export function formatMetric(value: number, format: MetricFormat): string {
  switch (format) {
    case "count":
      return nfInt.format(value);
    case "percent":
      return `${nfPct.format(value)} %`;
    case "currency_da":
      return `${nfInt.format(Math.round(value) / 100)} DA`;
    case "decimal":
    default:
      return nfDec.format(value);
  }
}

// ── Period → date window (client-side window over the snapshot history) ───────────

/** Earliest ISO date (inclusive) included by a period, relative to `now`. */
export function windowStart(period: Period, now: Date = new Date()): Date {
  const d = new Date(now);
  switch (period) {
    case "ytd":
      return new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    case "3mo": {
      const s = new Date(d);
      s.setUTCMonth(s.getUTCMonth() - 3);
      return s;
    }
    case "12mo":
    default: {
      const s = new Date(d);
      s.setUTCMonth(s.getUTCMonth() - 12);
      return s;
    }
  }
}

/**
 * Filter + sort (ascending by date) one metric's LAB-WIDE history into the chosen period window. Excludes
 * the NOVA-037 per-discipline rows (discipline_code != null) — the trend line is the lab-wide series; the
 * per-discipline breakdown lives in its own table.
 */
export function seriesForPeriod(
  history: ReadonlyArray<HistoryMetric>,
  metricType: MetricType,
  period: Period,
  now: Date = new Date(),
): HistoryMetric[] {
  const start = windowStart(period, now).getTime();
  return history
    .filter((h) => h.metric_type === metricType && h.discipline_code == null && new Date(h.date).getTime() >= start)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

/** Short axis label for a date (FR locale, day+month). LTR island in RTL. */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit" }).format(d);
}
